// Firebase Auth utilities
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  User as FirebaseUser,
  UserCredential
} from 'firebase/auth';
import { auth } from '../../config/firebase';

let currentUser: FirebaseUser | null = null;
let authInitialized = false;

// Google provider for OAuth
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

export const initAuth0 = async (): Promise<void> => {
  return new Promise(async (resolve) => {
    if (!auth) {
      console.error('🔥 Firebase Auth not initialized');
      authInitialized = true;
      resolve();
      return;
    }

    // Check for redirect result first with retry logic
    console.log('🔍 Checking for OAuth redirect result...');
    console.log('🌐 Current URL:', window.location.href);
    console.log('🌐 URL params:', new URLSearchParams(window.location.search).toString());
    console.log('🌐 Auth domain:', auth?.config?.authDomain);

    try {
      const { getRedirectResult } = await import('firebase/auth');

      // Retry logic for redirect result - sometimes it takes a moment
      let result = null;
      let retryCount = 0;
      const maxRetries = 3;

      while (!result && retryCount < maxRetries) {
        result = await getRedirectResult(auth);
        console.log(`🔍 getRedirectResult attempt ${retryCount + 1}:`, result ? 'USER FOUND' : 'NO RESULT');

        // Debug additional redirect result info
        if (result) {
          console.log('🎯 Redirect result details:', {
            user: result.user.email,
            providerId: result.providerId,
            operationType: result.operationType
          });
        } else {
          console.log('🔍 No redirect result - checking URL for auth fragments...');
          const url = new URL(window.location.href);
          const hashParams = new URLSearchParams(url.hash.substring(1));
          const searchParams = new URLSearchParams(url.search);
          console.log('🔍 URL hash params:', Object.fromEntries(hashParams.entries()));
          console.log('🔍 URL search params:', Object.fromEntries(searchParams.entries()));
        }

        if (!result && retryCount < maxRetries - 1) {
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        retryCount++;
      }

      if (result) {
        console.log('🎉 Firebase Auth: Redirect result found!', result.user.email);
        currentUser = result.user;
        // Store the user data immediately - SECURITY: Role from server, not hardcoded
        const token = await result.user.getIdToken();
        const userRole = await getUserRoleFromFirestore(result.user.uid);
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName || result.user.email?.split('@')[0] || 'User',
          photoURL: result.user.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${result.user.email}`,
          role: userRole // SECURITY: Server-validated role
        }));
        authInitialized = true;
        console.log('✅ Firebase Auth: Initialization complete via redirect');
        resolve();
        return;
      } else {
        console.log('ℹ️ No redirect result found after retries, continuing with normal auth state listener');
      }
    } catch (error) {
      console.error('❌ Firebase Auth: Error checking redirect result:', error);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      currentUser = user;
      
      if (user) {
        // Store the user data and get fresh token - SECURITY: Role from server
        try {
          const token = await user.getIdToken();
          const userRole = await getUserRoleFromFirestore(user.uid);
          localStorage.setItem('auth_token', token);
          localStorage.setItem('auth_user', JSON.stringify({
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'User',
            photoURL: user.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${user.email}`,
            role: userRole // SECURITY: Server-validated role
          }));
        } catch (error) {
          console.error('🔥 Error getting token on auth state change:', error);
        }
      } else {
        // Clear stored data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
      
      if (!authInitialized) {
        authInitialized = true;
        console.log('🔧 Firebase Auth: Initialization complete', user ? 'User signed in' : 'No user');
        resolve();
      }
    });

    // Don't clean up listener immediately, let it stay active
    setTimeout(() => {
      if (!authInitialized) {
        authInitialized = true;
        console.log('🔧 Firebase Auth: Timeout reached, initialization complete');
        resolve();
      }
    }, 3000); // 3 second timeout
  });
};

export const loginWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  if (!auth) throw new Error('Firebase Auth not initialized');
  return signInWithEmailAndPassword(auth, email, password);
};

export const signUpWithEmail = async (email: string, password: string): Promise<UserCredential> => {
  if (!auth) throw new Error('Firebase Auth not initialized');
  return createUserWithEmailAndPassword(auth, email, password);
};

export const loginWithGoogle = async (): Promise<UserCredential> => {
  if (!auth) throw new Error('Firebase Auth not initialized');
  
  // Use popup for development, redirect for production
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isLocalhost) {
    // Use popup for localhost development
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('🔥 Firebase Auth: Google popup sign-in successful');
      return result;
    } catch (error) {
      console.error('🔥 Firebase Auth: Popup sign-in error', error);
      throw error;
    }
  } else {
    // Use redirect for production
    console.log('🔄 Production OAuth: Using signInWithRedirect');
    console.log('🚀 Starting Google OAuth redirect...');

    const { signInWithRedirect } = await import('firebase/auth');
    await signInWithRedirect(auth, googleProvider);

    // This line will never be reached as the redirect happens immediately
    throw new Error('REDIRECT_INITIATED'); // This will be caught and handled
  }
};

export const login = () => {
  // This is called by the old AuthScreen - redirect to new auth
  window.location.href = '/auth';
};

export const logout = async (): Promise<void> => {
  try {
    console.log('🚪 Starting Firebase logout...');
    
    // Clear auth data FIRST to prevent routing issues
    currentUser = null;
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    console.log('✅ Local storage cleared');
    
    if (auth) {
      await signOut(auth);
      console.log('✅ Firebase signOut successful');
    }
    
    console.log('🔄 Attempting to redirect to home page...');
    
    // Force a full page refresh to ensure clean state
    window.location.href = '/';
    
  } catch (error) {
    console.error('🔥 Firebase Auth: Logout error', error);
    
    // Even if Firebase logout fails, clear local data and redirect
    currentUser = null;
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    
    console.log('🔄 Force redirect after error...');
    window.location.href = '/';
    
    throw error;
  }
};

export const getUser = () => {
  if (currentUser) {
    return {
      uid: currentUser.uid,
      email: currentUser.email,
      displayName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
      photoURL: currentUser.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${currentUser.email}`,
      role: 'free' // Default role
    };
  }
  
  // Try to get from localStorage if current user is null
  const storedUser = localStorage.getItem('auth_user');
  if (storedUser) {
    try {
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  }
  
  return null;
};

export const isAuthenticated = (): boolean => {
  return currentUser !== null;
};

// SECURITY: Server-side role validation function
const getUserRoleFromFirestore = async (uid: string): Promise<string> => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../../config/firebase');

    // Check user's subscription status in Firestore
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      // Return subscription tier as role, with fallback to 'free'
      return userData.subscriptionTier || userData.role || 'free';
    }

    // Default to free if no user document found
    return 'free';
  } catch (error) {
    console.error('🔥 Error fetching user role from Firestore:', error);
    // Fail secure - default to free if role fetch fails
    return 'free';
  }
};

// SECURITY: Verify role from server before sensitive operations
export const verifyUserRole = async (): Promise<string> => {
  if (!currentUser) return 'free';
  return getUserRoleFromFirestore(currentUser.uid);
};

export const getIdToken = (): string | null => {
  if (!currentUser || !auth) return null;
  
  try {
    // Return the cached token if available, otherwise return null
    // In a real implementation, you might want to check if token is still valid
    return localStorage.getItem('auth_token') || null;
  } catch (error) {
    console.error('🔥 Firebase Auth: Error getting ID token', error);
    return null;
  }
};

export const getIdTokenAsync = async (): Promise<string | null> => {
  if (!currentUser || !auth) return null;
  
  try {
    const token = await currentUser.getIdToken();
    localStorage.setItem('auth_token', token);
    return token;
  } catch (error) {
    console.error('🔥 Firebase Auth: Error getting ID token', error);
    return null;
  }
};

export const getAccessToken = (): string | null => {
  // Firebase doesn't have separate access tokens, use ID token
  return getIdToken();
};

export const fetchUserProfile = async () => {
  return getUser();
};

// Subscribe to auth state changes
export const subscribeToAuthChanges = (callback: (user: any) => void) => {
  if (!auth) return () => {};
  
  return onAuthStateChanged(auth, (user) => {
    currentUser = user;
    callback(getUser());
  });
};
